import { OrganizationSwitcher, UserButton } from "@clerk/clerk-react"
import {
  ActionIcon,
  AppShell,
  Group,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core"
import { IconHelpCircle, IconMoon, IconSun } from "@tabler/icons-react"
import { useEffect, useMemo } from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "react-router-dom"

import {
  SIDEBAR_ITEMS,
  SidebarItem,
} from "@costory/front/components/layout/constants"
import { usePageContext } from "@costory/front/contexts/PageContext"

interface FlattenedRoute {
  path: string
  name: string
  tooltip?: string
}
export const AppHeader = () => {
  const location = useLocation()

  const { pageTitle, tooltipText } = useMemo(() => {
    const flattenRoutes = (
      routes: SidebarItem[],
      basePath = "",
    ): FlattenedRoute[] =>
      routes.flatMap((route) => {
        const fullPath = route.path ? `${basePath}/${route.path}` : basePath
        const flattened = route.path
          ? [{ path: fullPath, name: route.name, tooltip: route.tooltip }]
          : []
        const subRoutes = route.links
          ? flattenRoutes(route.links, fullPath)
          : []
        return [...flattened, ...subRoutes]
      })
    const currentRoute = flattenRoutes(SIDEBAR_ITEMS).find((route) =>
      route.path.includes(location.pathname),
    )

    return {
      pageTitle: currentRoute?.name || "",
      tooltipText: currentRoute?.tooltip || "",
    }
  }, [location.pathname])

  const { colorScheme, toggleColorScheme } = useMantineColorScheme()

  const { secondaryTitle, setSecondaryTitle } = usePageContext()

  // Reset secondary title when navigating to a new page
  useEffect(() => {
    setSecondaryTitle(undefined)
  }, [location.pathname, setSecondaryTitle])

  return (
    <>
      <Helmet>
        <title>Finops - {pageTitle}</title>
      </Helmet>
      <AppShell.Header h={64} bg="surfaceCard">
        <Group px={32} py={16} justify="space-between" wrap="nowrap">
          <Group gap={0} align="center" miw={0}>
            <Title
              size="h3"
              fw={600}
              w="100%"
              lineClamp={1}
              style={{
                whiteSpace: "nowrap",
              }}
            >
              {pageTitle}
              {tooltipText && (
                <Tooltip label={tooltipText}>
                  <ThemeIcon c="primaryHighlight" mt={2} size="sm">
                    <IconHelpCircle />
                  </ThemeIcon>
                </Tooltip>
              )}
              {secondaryTitle && " - " + secondaryTitle}
            </Title>
          </Group>
          <Group wrap="nowrap">
            <ActionIcon c="textSecondary" onClick={toggleColorScheme}>
              {colorScheme === "dark" ? <IconSun /> : <IconMoon />}
            </ActionIcon>
            <OrganizationSwitcher
              hidePersonal
              afterSelectOrganizationUrl="/home"
            />
            <UserButton />
          </Group>
        </Group>
      </AppShell.Header>
    </>
  )
}
